// src/store/slices/uiSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type Theme = 'dark' | 'light';
export type Density = 'comfortable' | 'compact';

interface UIState {
  sidebarOpen: boolean;
  searchQuery: string;
  filterAssign: string;
  filterPriority: string;
  filterStatus: string;
  sortBy: 'priority' | 'due' | 'new' | 'az';
  onlineUsers: Array<{ role: string }>;
  syncStatus: 'live' | 'sync' | 'off' | 'idle';
  theme: Theme;
  density: Density;
}

// UI Enhancements (Phase 11) — theme/density are the one bit of UI state that
// must survive a refresh on its own, so they're read from localStorage here
// rather than round-tripping through the backend.
function readStoredTheme(): Theme {
  return localStorage.getItem('om-theme') === 'light' ? 'light' : 'dark';
}
function readStoredDensity(): Density {
  return localStorage.getItem('om-density') === 'compact' ? 'compact' : 'comfortable';
}

const initialState: UIState = {
  sidebarOpen: true,
  searchQuery: '',
  filterAssign: 'all',
  filterPriority: 'all',
  filterStatus: 'all',
  sortBy: 'priority',
  onlineUsers: [],
  syncStatus: 'idle',
  theme: readStoredTheme(),
  density: readStoredDensity(),
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => { state.sidebarOpen = !state.sidebarOpen; },
    setSearchQuery: (state, action: PayloadAction<string>) => { state.searchQuery = action.payload; },
    setFilter: (state, action: PayloadAction<{ key: keyof UIState; value: string }>) => {
      (state as Record<string, unknown>)[action.payload.key] = action.payload.value;
    },
    setSortBy: (state, action: PayloadAction<UIState['sortBy']>) => { state.sortBy = action.payload; },
    setOnlineUsers: (state, action: PayloadAction<Array<{ role: string }>>) => {
      state.onlineUsers = action.payload;
    },
    setSyncStatus: (state, action: PayloadAction<UIState['syncStatus']>) => {
      state.syncStatus = action.payload;
    },
    setTheme: (state, action: PayloadAction<Theme>) => {
      state.theme = action.payload;
      localStorage.setItem('om-theme', action.payload);
    },
    setDensity: (state, action: PayloadAction<Density>) => {
      state.density = action.payload;
      localStorage.setItem('om-density', action.payload);
    },
  },
});

export const {
  toggleSidebar,
  setSearchQuery, setFilter, setSortBy,
  setOnlineUsers, setSyncStatus, setTheme, setDensity,
} = uiSlice.actions;
export default uiSlice.reducer;
